import type {
  OrganizationType,
  OrganizationTypeConflictListDto,
  ResolveOrganizationTypeConflictResponse,
} from '@signalvote/shared';
import { apiJson, apiVoid } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { refusal } from './useFinanceContributions';
import { useRefreshFinanceAfter } from './useFinanceSettings';

// Issue #350: the data path for the inline contact-page fix-it alert that surfaces a contact-merge
// OrganizationType conflict (#348). Because BOTH candidate values were recorded, resolving is a
// single tap: "It's {kept}" / "It's {discarded}" / "Dismiss". State is audit-derived and shared
// with the filing preflight, so resolving/dismissing clears both.

/** Every open contact-merge OrganizationType conflict in the campaign. */
const organizationTypeConflictsRead = defineQuery({
  key: () => ['finance', 'organization-type-conflicts'],
  reads: 'GET /api/finance/organization-type-conflicts',
  staleTime: 30_000,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<OrganizationTypeConflictListDto>('/api/finance/organization-type-conflicts', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch organization-type conflicts',
    }),
});

export function useOrganizationTypeConflicts() {
  return organizationTypeConflictsRead(undefined);
}

/**
 * POST /{contactId}/resolve: "It's {type}". A row write on the contact: a lost race fails as
 * `stale` and refreshes the reads holding the contact, the conflict list included. The new type
 * changes how the contact's gifts are itemized in report previews and the payments reads, which are
 * not defined reads yet, so it also refreshes the undeclared finance reads.
 */
const resolveOrganizationTypeConflict = defineWrite({
  row: 'FinanceContact',
  send: (contactId, concurrencyVersion: number, { organizationType }: { organizationType: OrganizationType }) =>
    apiJson<ResolveOrganizationTypeConflictResponse>(`/api/finance/organization-type-conflicts/${contactId}/resolve`, {
      method: 'POST',
      json: { organizationType, concurrencyVersion },
      error: refusal('Failed to resolve organization-type conflict'),
    }),
});

export function useResolveOrganizationTypeConflict() {
  return useRefreshFinanceAfter(resolveOrganizationTypeConflict());
}

/**
 * POST /{contactId}/dismiss: records the dismissal without changing the type (204). Idempotent. It
 * writes only the audit row, which the conflict list reads.
 */
export const useDismissOrganizationTypeConflict = defineWrite({
  row: 'FinanceContact',
  send: (contactId, concurrencyVersion: number) =>
    apiVoid(`/api/finance/organization-type-conflicts/${contactId}/dismiss`, {
      method: 'POST',
      json: { concurrencyVersion },
      error: refusal('Failed to dismiss organization-type conflict'),
    }),
});
